import { useInViewport } from 'ahooks'
import { useRequest, usePagination } from 'alova/client'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Input, Popover, Spin, message } from 'antd'
import AIcon from '@/components/A/AIcon/AIcon'
import ASearch from '@/components/A/ASearch/ASearch'
import ASelect from '@/components/A/ASelect/ASelect'
import styles from './StatisticCompareStartPage.module.less'
import { api } from '@/api'
import { INTERNAL_HOST } from '@/commonConst'
import useProductType from '@/helpers/commonHooks/useProductType'
import { commonUtils } from '@/helpers/commonUtils'
import { dayjsService } from '@/services/dayjsService'
import { getProductDashboardLink } from '@/router/pageConst'
import { isPortraitProductCategory } from '@/helpers/productCategory'
import { useNormOptions } from '@/helpers/commonHooks/useNormOptions'

const PAGE_SIZE = 20
const MAX_SELECTED_PROJECTS = 10
const MAX_REPORT_NAME_LENGTH = 50

function StatisticCompareStartPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { productType } = useProductType()
  const normOptions = useNormOptions()
  const isPortraitMode = isPortraitProductCategory(productType)
  const reportId = Number(searchParams.get('reportId') || 0)
  const fromCompareInfo = searchParams.get('from') === 'info'
  const isEditMode = reportId > 0
  const [reportName, setReportName] = useState('')
  const [normType, setNormType] = useState<1 | 2>()
  const [keyword, setKeyword] = useState('')
  const [creativeTypeFilter, setCreativeTypeFilter] = useState<ProjectModels.ListCreativeTypeFilter>('all')
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([])
  const [tabCounts, setTabCounts] = useState({ all: 0, general: 0, splash: 0 })
  const [hasNext, setHasNext] = useState(false)
  const [projectListOverflow, setProjectListOverflow] = useState(false)
  const projectListAreaRef = useRef<HTMLDivElement>(null)
  const paginationSentinelRef = useRef<HTMLDivElement>(null)
  const [paginationSentinelInView] = useInViewport(paginationSentinelRef)
  const loadMoreLockRef = useRef(false)
  const lastRequestedPageRef = useRef(1)
  const initializedRef = useRef(false)
  const selectedProjectMapRef = useRef<Map<number, ProjectModels.ProjectListItem>>(new Map())

  const {
    data: projectList,
    send: sendListRequest,
    abort: abortListRequest,
    reload: reloadList,
    update: updateListData,
    page,
    total,
    loading,
  } = usePagination(
    (pageNo, pageSize) => {
      const param: Parameters<typeof api.project.projectList>[0] = {
        pageNo,
        pageSize,
        taskType: isPortraitMode ? 'portrait' : 'landscape',
        projectCategory: productType,
        status: 'completed',
      }
      if (keyword) {
        param.keyword = keyword
      }
      if (isPortraitMode) {
        if (creativeTypeFilter === 'general') {
          param.durationCategory = 'ten_plus'
        } else if (creativeTypeFilter === 'splash') {
          param.durationCategory = 'five_to_ten'
        }
      }
      return api.project.projectList(param)
    },
    {
      initialPage: 1,
      initialPageSize: PAGE_SIZE,
      data: (res) => res.objects,
      total: (res) => {
        setHasNext(res.hasNext)
        setTabCounts({
          all: res.allCount ?? res.totalCount ?? 0,
          general: res.generateCount ?? 0,
          splash: res.splashCount ?? 0,
        })
        return res.totalCount
      },
      append: true,
      immediate: false,
      preloadNextPage: false,
      preloadPreviousPage: false,
    }
  )

  const {
    data: reportDetail,
    send: sendReportDetail,
  } = useRequest(
    api.compare.compareReportDetail,
    {
      immediate: false,
      initialData: { data: undefined },
    }
  )
  const sendReportDetailRef = useRef(sendReportDetail)

  const goBackToPreviousPage = useCallback(() => {
    if (isEditMode && fromCompareInfo) {
      navigate(`${getProductDashboardLink(productType, 'statisticCompareInfo')}?reportId=${reportId}`)
      return
    }
    navigate(getProductDashboardLink(productType, 'statisticCompare'))
  }, [fromCompareInfo, isEditMode, navigate, productType, reportId])

  const resetLoadMoreState = useCallback(() => {
    loadMoreLockRef.current = false
    lastRequestedPageRef.current = 1
  }, [])

  const updateProjectListOverflow = useCallback(() => {
    const listArea = projectListAreaRef.current
    if (!listArea) {
      setProjectListOverflow(false)
      return
    }
    setProjectListOverflow(listArea.scrollHeight > listArea.clientHeight)
  }, [])

  useEffect(() => {
    let cancelled = false
    resetLoadMoreState()
    if (!isEditMode) {
      setSelectedProjectIds([])
      selectedProjectMapRef.current = new Map()
    }
    sendListRequest(1, PAGE_SIZE).finally(() => {
      if (cancelled) {
        abortListRequest()
      }
    })
    return () => {
      cancelled = true
      abortListRequest()
    }
  }, [productType, sendListRequest, abortListRequest, resetLoadMoreState, isEditMode])

  useEffect(() => {
    sendReportDetailRef.current = sendReportDetail
  }, [sendReportDetail])

  useEffect(() => {
    if (isEditMode) {
      sendReportDetailRef.current(reportId)
    }
  }, [isEditMode, reportId])

  useEffect(() => {
    if (!reportDetail?.data) {
      return
    }
    const report = reportDetail.data
    setReportName(report.reportName)
    setNormType(report.norm)
    setSelectedProjectIds(report.projects.map((project) => project.id))
    selectedProjectMapRef.current = new Map(report.projects.map((project) => [project.id, project]))
  }, [reportDetail])

  useEffect(() => {
    projectList.forEach((project) => {
      if (selectedProjectIds.includes(project.id)) {
        selectedProjectMapRef.current.set(project.id, project)
      }
    })
  }, [projectList, selectedProjectIds])

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true
      return
    }
    resetLoadMoreState()
    reloadList()
  }, [keyword, creativeTypeFilter, reloadList, resetLoadMoreState])

  useEffect(() => {
    if (!loading) {
      loadMoreLockRef.current = false
    }
  }, [loading])

  useEffect(() => {
    updateProjectListOverflow()
    const listArea = projectListAreaRef.current
    if (!listArea || typeof ResizeObserver === 'undefined') {
      return
    }
    const resizeObserver = new ResizeObserver(updateProjectListOverflow)
    resizeObserver.observe(listArea)
    Array.from(listArea.children).forEach((child) => {
      resizeObserver.observe(child)
    })
    return () => {
      resizeObserver.disconnect()
    }
  }, [projectList, loading, hasNext, isPortraitMode, updateProjectListOverflow])

  useEffect(() => {
    if (!paginationSentinelInView || loading || !hasNext) {
      return
    }
    const nextPage = lastRequestedPageRef.current + 1
    if (loadMoreLockRef.current) {
      return
    }
    loadMoreLockRef.current = true
    lastRequestedPageRef.current = nextPage
    updateListData({ page: nextPage })
  }, [paginationSentinelInView, loading, hasNext, updateListData])

  const handleProjectToggle = (projectId: number) => {
    const project = projectList.find((item) => item.id === projectId)
    if (project) {
      selectedProjectMapRef.current.set(projectId, project)
    }
    setSelectedProjectIds((current) => {
      if (current.includes(projectId)) {
        return current.filter((id) => id !== projectId)
      }
      if (current.length >= MAX_SELECTED_PROJECTS) {
        message.warning(t('dashboard.dataComparisonStartPage.maxSelectedWarning', {
          defaultValue: '最多选择 10 个项目',
        }))
        return current
      }
      return [...current, projectId]
    })
  }

  const handleStartCompare = () => {
    if (!reportName.trim()) {
      message.warning(t('dashboard.dataComparisonStartPage.reportNameRequired', {
        defaultValue: '请输入对比报告名称',
      }))
      return
    }
    if (!normType) {
      message.warning(t('dashboard.dataComparisonStartPage.normRequired', {
        defaultValue: '请选择基准值',
      }))
      return
    }
    if (selectedProjectIds.length === 0) {
      message.warning(t('dashboard.dataComparisonStartPage.projectRequired', {
        defaultValue: '请选择项目',
      }))
      return
    }
    const request = isEditMode
      ? api.compare.compareReportUpdate({
        id: reportId,
        reportName: reportName.trim(),
        norm: normType,
        projectIds: selectedProjectIds,
      })
      : api.compare.compareReportCreate({
        projectCategory: productType,
        reportName: reportName.trim(),
        norm: normType,
        projectIds: selectedProjectIds,
      })
    request.send().then((res) => {
      const nextReportId = res.data.id
      navigate(`${getProductDashboardLink(productType, 'statisticCompareInfo')}?reportId=${nextReportId}`)
    })
  }

  const showInitialLoading = page === 1 && loading && projectList.length === 0
  const isEmpty = !loading && total === 0

  return (
    <div className={styles.statisticCompareStartPage} data-testid='statistic-compare-start-page'>
      <button
        type='button'
        className={styles.backButton}
        data-testid='statistic-compare-start-back-btn'
        onClick={goBackToPreviousPage}
      >
        <span className={styles.backIconBox}>
          <AIcon type='icon-line-global_left' width={14} height={14} />
        </span>
        <span>{t('dashboard.dataComparisonStartPage.back', { defaultValue: '返回' })}</span>
      </button>

      <div className={styles.contentPanel}>
        <section className={styles.basicInfoSection}>
          <SectionTitle>
            {t('dashboard.dataComparisonStartPage.basicInfo', { defaultValue: '基本信息' })}
          </SectionTitle>
          <div className={styles.formGrid}>
            <FieldLabel
              required
              label={t('dashboard.dataComparisonStartPage.reportName', { defaultValue: '报告名称:' })}
            >
              <Input
                className={styles.formInput}
                data-testid='statistic-compare-report-name-input'
                value={reportName}
                placeholder={t('dashboard.dataComparisonStartPage.reportNamePlaceholder', {
                  defaultValue: '请输入对比报告名称',
                })}
                maxLength={MAX_REPORT_NAME_LENGTH}
                onChange={(event) => setReportName(event.target.value.slice(0, MAX_REPORT_NAME_LENGTH))}
              />
            </FieldLabel>
            <FieldLabel
              required
              label={t('dashboard.dataComparisonStartPage.norm', { defaultValue: '基准值:' })}
            >
              <ASelect
                className={styles.normSelect}
                testId='statistic-compare-norm-select'
                value={normType}
                allowClear
                placeholder={t('dashboard.dataComparisonStartPage.normPlaceholder', {
                  defaultValue: '请选择基准值',
                })}
                options={normOptions.map((opt) => {
                  if (opt.value === 2) {
                    const label = (
                      <span>
                        <span>{opt.label}</span>
                        <Popover
                          overlayClassName={styles.normPopover}
                          arrow={{ pointAtCenter: true }}
                          placement='top'
                          content={
                            <p className={styles.normDesc}>{t('dashboard.projectCreatePage.message.normDesc')}</p>
                          }
                        >
                          <span className={styles.normPopoverIcon}>
                            <AIcon type='icon-line-global_question' width={16} height={16} />
                          </span>
                        </Popover>
                      </span>
                    )
                    return { ...opt, label }
                  }
                  return opt
                })}
                labelRender={(option) => {
                  return normOptions.find((opt) => opt.value === option.value)?.label ?? option.label
                }}
                onChange={(value) => setNormType(value as 1 | 2 | undefined)}
              />
              <p className={styles.fieldTip}>
                {t('dashboard.dataComparisonStartPage.normTip', {
                  defaultValue: '请选择项目对比时使用的基准值',
                })}
              </p>
            </FieldLabel>
          </div>
        </section>

        <div className={styles.divider} />

        <section className={styles.projectSection}>
          <SectionTitle>
            {t('dashboard.dataComparisonStartPage.selectProject', { defaultValue: '选择项目' })}
          </SectionTitle>
          <div className={styles.projectToolbar}>
            {isPortraitMode ? (
              <div className={styles.portraitTabs}>
                {([
                  { key: 'all', label: t('dashboard.projectPage.tabAllCreative', { count: tabCounts.all }) },
                  { key: 'general', label: t('dashboard.projectPage.tabGeneralCreative', { count: tabCounts.general }) },
                  { key: 'splash', label: t('dashboard.projectPage.tabSplashCreative', { count: tabCounts.splash }) },
                ] as const).map((tab) => (
                  <button
                    key={tab.key}
                    type='button'
                    data-testid={`statistic-compare-creative-type-${tab.key}-tab`}
                    className={styles.portraitTab}
                    data-active={creativeTypeFilter === tab.key}
                    onClick={() => setCreativeTypeFilter(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : (
              <p className={styles.projectTotal}>
                {t('dashboard.projectPage.totalDesc', { total })}
              </p>
            )}
            <ASearch
              className={styles.searchInput}
              testId='statistic-compare-project-search-input'
              placeholder={t('dashboard.dataComparisonStartPage.searchPlaceholder', {
                defaultValue: '输入项目/广告/品牌名称搜索',
              })}
              allowClear
              onSearch={(value) => {
                setKeyword(value ? `${value}` : '')
              }}
            />
          </div>

          <div
            ref={projectListAreaRef}
            className={classNames(styles.projectListArea, {
              [styles.portrait]: isPortraitMode,
              [styles.landscape]: !isPortraitMode,
            })}
          >
            {showInitialLoading ? (
              <div className={styles.loadingBox}><Spin /></div>
            ) : null}
            {isEmpty ? (
              <div className={styles.emptyBox}>
                <img src={commonUtils.getImagePath('projectPage/project-list-empty.png')} alt='' />
                <p>{t('dashboard.projectPage.noRelevantContent')}</p>
              </div>
            ) : null}
            {!showInitialLoading && !isEmpty ? (
              <>
                <div
                  className={classNames(styles.cardList, {
                    [styles.portrait]: isPortraitMode,
                    [styles.landscape]: !isPortraitMode,
                  })}
                  data-testid='statistic-compare-project-card-list'
                >
                  {projectList.map((project) => (
                    <SelectableProjectCard
                      key={project.id}
                      project={project}
                      selected={selectedProjectIds.includes(project.id)}
                      isPortrait={isPortraitMode}
                      onToggle={() => handleProjectToggle(project.id)}
                    />
                  ))}
                </div>
                {hasNext ? (
                  <div className={styles.loadingBox} ref={paginationSentinelRef}>
                    {loading && page > 1 ? <Spin /> : null}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </section>

        <div className={classNames(styles.footerBar, {
          [styles.withShadow]: projectListOverflow,
        })}
        >
          <p className={styles.selectedCount}>
            {t('dashboard.dataComparisonStartPage.selectedCountPrefix', { defaultValue: ' 已选择 ' })}
            <span>{selectedProjectIds.length}</span>
            {t('dashboard.dataComparisonStartPage.selectedCountSuffix', {
              max: MAX_SELECTED_PROJECTS,
              defaultValue: ' 个项目，最多选择{{max}}个项目',
            })}
          </p>
          <div className={styles.footerActions}>
            <button
              type='button'
              className={styles.primaryButton}
              data-testid='statistic-compare-create-btn'
              onClick={handleStartCompare}
            >
              {isEditMode
                ? t('dashboard.dataComparisonStartPage.save', { defaultValue: '保存' })
                : t('dashboard.dataComparisonStartPage.startCompare', { defaultValue: '开始对比' })}
            </button>
            <button
              type='button'
              className={styles.cancelButton}
              data-testid='statistic-compare-cancel-btn'
              onClick={goBackToPreviousPage}
            >
              {t('dashboard.dataComparisonStartPage.cancel', { defaultValue: '取消' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function FieldLabel(props: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className={styles.fieldLabel}>
      <span className={styles.labelText}>
        {props.required ? <span className={styles.requiredMark}>*</span> : null}
        {props.label}
      </span>
      {props.children}
    </label>
  )
}

function SectionTitle(props: { children: React.ReactNode; size?: 'small' | 'normal' }) {
  return (
    <h2 className={classNames(styles.sectionTitle, {
      [styles.small]: props.size === 'small',
    })}
    >
      {props.children}
    </h2>
  )
}

function SelectableProjectCard(props: {
  project: ProjectModels.ProjectListItem
  selected: boolean
  isPortrait: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const { project, selected, isPortrait, onToggle } = props
  const creativeTypeLabel = project.durationCategory === 'five_to_ten'
    ? t('dashboard.projectPage.splashCreativeLabel', { defaultValue: 'Splash Creative' })
    : t('dashboard.projectPage.generalCreativeLabel', { defaultValue: 'General Creative' })
  const dateText = INTERNAL_HOST.includes(location.hostname)
    ? dayjs(project.createdAt * 1000).format('YYYY-MM-DD HH:mm')
    : dayjsService.convertUtcTimestampToTZ(project.createdAt, 'YYYY-MM-DD HH:mm')

  return (
    <button
      type='button'
      className={classNames(styles.projectCard, {
        [styles.selected]: selected,
        [styles.portrait]: isPortrait,
        [styles.landscape]: !isPortrait,
      })}
      data-testid={`statistic-compare-project-card-${project.id}`}
      onClick={onToggle}
    >
      <div className={styles.coverBox}>
        <div className={styles.brandBadge}>{project.brandName}</div>
        <CoverImage
          src={project.coverPath || commonUtils.getImagePath('common/common-empty.png')}
          alt=''
        />
        <div className={styles.durationBadge}>
          <AIcon type='icon-fill-global_play2' width={10} height={10} />
          <span>{dayjs(project.adDuration * 1000).format('m:ss')}</span>
        </div>
      </div>
      <div className={styles.projectInfo}>
        <div className={styles.projectTitleRow}>
          <span className={styles.projectTitle}>{project.projectName}</span>
          <Score
            score={project.status === 'completed' && project.score !== 0 ? project.score : '-'}
            meanScore={
              project.status === 'completed' && project.meanScore !== 0
                ? project.meanScore
                : '-'
            }
          />
        </div>
        {isPortrait ? (
          <span className={styles.creativeTypeTag}>
            <AIcon type='icon-a-chuangyilinggan1' />
            {creativeTypeLabel}
          </span>
        ) : null}
        <div className={styles.projectDate}>
          <span>{dateText}</span>
        </div>
      </div>
      {selected ? (
        <span className={styles.selectedCheckIcon}>
          <AIcon width={16} height={16} type='icon-line-global_check' />
        </span>
      ) : null}
    </button>
  )
}

function CoverImage(props: { src: string; alt: string }) {
  const fallbackSrc = commonUtils.getImagePath('common/common-empty.png')
  const [currentSrc, setCurrentSrc] = useState(props.src || fallbackSrc)

  useEffect(() => {
    setCurrentSrc(props.src || fallbackSrc)
  }, [props.src, fallbackSrc])

  return (
    <img
      className={styles.coverImage}
      src={currentSrc}
      alt={props.alt}
      loading='eager'
      decoding='async'
      onError={() => {
        if (currentSrc !== fallbackSrc) {
          setCurrentSrc(fallbackSrc)
        }
      }}
    />
  )
}

function Score({ score, meanScore }: { score: number | '-'; meanScore: number | '-' }) {
  const lower = typeof score === 'number' && typeof meanScore === 'number' && score < meanScore
  return (
    <div className={styles.score}>
      <img
        className={styles.scoreBg}
        src={commonUtils.getImagePath(
          lower
            ? 'projectPage/project-card-score-lower.png'
            : 'projectPage/project-card-score-high.png'
        )}
        alt=''
      />
      <span className={classNames(styles.scoreText, { [styles.lower]: lower })}>{score}</span>
    </div>
  )
}

export default StatisticCompareStartPage
